<template>
  <div class="form-group">
    <div v-if="!isTypeAnswerSlide">
      <label v-if="isLabelSlideOptionsInputVisible" class="label-option">
        {{ optionLabel }}
        <span
          class="title-table-presentation-tooltip result-layout-tooltip tooltip-on-hover"
        >
          <span
            v-tooltip="optionTooltip"
            class="tooltip-btn"
          />

          <i class="fas fa-question-circle" />
        </span>
      </label>

      <template
        v-if="!isMatchPairsOrCorrectOrderSlide"
      >
        <draggable
          v-if="isDraggableSlideOptionsVisible"
          v-model="slideActive.SlideOptions"
          class="list-group"
          tag="ul"
          v-bind="dragOptions"
          :disabled="isSortingSlideOptions"
          draggable=".draggable-item"
          @start="drag = true"
          @end="drag = false"
          @change="handleSortOptions"
        >
          <li
            v-for="(item, index) in slideActive.SlideOptions"
            :key="'item_option_' + item.index +' '+ keyInput + index"
            :class="{ 'd-none': isHideOptions(index), 'draggable-item': item.id }"
          >
            <div class="mb-2 input-group input-option">
              <div
                class="input-group-prepend handle-drag"
                :class="{ 'disabled': isSortingSlideOptions || !item.id }"
              >
                <div class="input-group-text horizontal layout center center-justified">
                  <i class="fas fa-arrows-alt-v" />
                </div>
              </div>
              <div class="input-content-wrapper">
                <div
                  v-if="isCheckCorrectAnswerVisible && !isShowCorrectCheckAutocomplete(item)"
                  @mouseleave="onClickOutSideCheckCorrect(item)"
                  v-tooltip="{
                    content: tooltipCheckCorrect,
                    offset: 0,
                  }"
                  class="check-correct-answer horizontal layout center center-justified"
                  :class="{ correct: item.correct }"
                  @click="checkCorrectOption(item)"
                >
                  <CustomTooltip
                    text="Please add text or image to mark it correct"
                    v-if="item.showErrorCheckCorrect"
                  />
                  <span class="checkmark">
                    <i class="fas fa-check"></i>
                  </span>
                </div>
                <div
                  v-if="isShowCorrectCheckAutocomplete(item)"
                  class="check-correct-answer horizontal layout center center-justified correct-autocomplete"
                >
                  <span class="checkmark">
                    <i class="fas fa-check"></i>
                  </span>
                </div>
                <InputText
                  :id="item.id"
                  :key="item.randomIndexId || item.id || 'input_title_option' + index + item.index + keyInput"
                  :val="item.title"
                  :index="index"
                  :place-holder="`${optionPlaceHolder} ${index + 1}`"
                  :type="'input-option'"
                  :id-name="'multipleChoiceIndex'"
                  :inputId="`slide-option-${item.id}`"
                  :data-inputId="`slide-option-${item.id}`"
                  :randomIndexId="item.randomIndexId"
                  @interface="handleUpdateOptionTitle"
                  @add-option="$emit('backupAddOptionFromTab')"
                />

                <SlideOptionImageButton
                  v-if="isSlideOptionUploadImageButtonVisible"
                  :slideOption="item"
                  :index="index"
                />
              </div>

              <div
                class="ml-2 input-group-prepend pointer"
                :class="{ 'not-allowed': isDeleteOptionNotAllowed }"
              >
                <div
                  class="input-group-text horizontal layout center center-justified"
                  :class="{ 'disabled': isDeleteOptionNotAllowed }"
                  @click="$emit('deleteOption', item, index)"
                >
                  <i class="far fa-trash-alt" />
                </div>
              </div>
            </div>
          </li>
        </draggable>
        <aha-antd-button
          v-if="isAddSlideOptionButtonVisible"
          v-aha-emit-action
          name="addOption"
          class="add-button"
          iconName="add"
          variant="secondary"
          :iconWidth="16"
          :iconHeight="16"
          :class="isAllowPickingMoreThanOneOptionsVisible ? 'mb-3' : 'mb-1'"
          @click="$emit('addOption')"
        >
          <span>{{ $i18nPresenter.t('Add') }}</span>
        </aha-antd-button>

        <ConvertQuizNoteBox
          v-if="isShowConvertQuizNoteBox"
          class="mb-3"
        />
      </template>

      <MatchPairSlideOptions v-if="slideActive.slideType === 'matchPairs'" class="mb-4" />
      <CorrectOrderSlideOptions v-if="slideActive.slideType === 'correctOrder'" class="mb-4" />
    </div>

    <OptionsPickingConfig
      v-if="isAllowPickingMoreThanOneOptionsVisible"
      @updateSettingSlide="handleUpdateSettingSlide"
    />

    <QuizTypeOption v-if="isTypeAnswerSlide" />
    <QuizTypeAnswerInfo v-if="isTypeAnswerSlide" />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import InputText from '@/components/presentations/InputText.vue'
import ConvertQuizNoteBox from '@/components/presentations/ConvertQuizNoteBox.vue'
import MatchPairSlideOptions from '@/components-v2/presentation-editor/side-right/MatchPairSlideOptions.vue'
import CorrectOrderSlideOptions from '@/components-v2/presentation-editor/side-right/CorrectOrderSlideOptions.vue'
import SlideOptionImageButton from '@/components-v2/presentation-editor/side-right/SlideOptionImageButton.vue'
import OptionsPickingConfig from '@/components-v2/presentation-editor/side-right/OptionsPickingConfig.vue'
import QuizTypeOption from '@/components/presentations/QuizTypeOption.vue'
import QuizTypeAnswerInfo from '@/components/presentations/QuizTypeAnswerInfo.vue'
import CustomTooltip from '@/components-v2/common/tooltip/tooltip.vue'

const MarkAsCorrectAnswer = 'Mark as correct answer'

export default {
  components: {
    draggable,
    InputText,
    ConvertQuizNoteBox,
    MatchPairSlideOptions,
    CorrectOrderSlideOptions,
    SlideOptionImageButton,
    OptionsPickingConfig,
    QuizTypeOption,
    QuizTypeAnswerInfo,
    CustomTooltip,
  },
  props: {
    isSortingSlideOptions: {
      type: Boolean,
      default: false,
    },
    openTooltipImage: {
      type: String,
      default: null,
    },
    optionLabel: {
      type: String,
      default: 'Options',
    },
    optionTooltip: {
      type: String,
      default: '',
    },
    optionPlaceHolder: {
      type: String,
      default: 'Option',
    },
    keyInput: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      drag: false,
      countCorrectOption: 0,
      tooltipCheckCorrect: MarkAsCorrectAnswer,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      currentUser: (state) => state.currentUser,
      presentation: (state) => state.presentation,

    }),
    ...mapGetters(['getSlideOptionsActive']),
    ...mapGetters('aiGenerateSlideOptions', ['autoCompleteSlideOption']),
    ...mapGetters('featureToggles', [
      'canUseNewAutocomplete',
    ]),
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        delay: 0,
        ghostClass: 'ghost',
        handle: '.handle-drag',
      }
    },
    isTypeAnswerSlide() {
      return this.slideActive.type === 'pickAnswer' && this.slideActive.slideType === 'typeAnswer'
    },
    isLabelSlideOptionsInputVisible() {
      const slideTypes = ['scale', 'multipleChoice', 'imageChoice', 'bulletPoints', 'pickAnswer']
      return slideTypes.includes(this.slideActive.type)
    },
    isMatchPairsOrCorrectOrderSlide() {
      const slideTypes = ['matchPairs', 'correctOrder']
      return slideTypes.includes(this.slideActive.slideType)
    },
    isDraggableSlideOptionsVisible() {
      return this.isLabelSlideOptionsInputVisible && this.slideActive.SlideOptions.length
    },
    isSlideOptionUploadImageButtonVisible() {
      const { type, slideType } = this.slideActive
      return type === 'imageChoice' || (type === 'pickAnswer' && (slideType === 'imageChoice' || !slideType)) || type === 'multipleChoice'
    },
    isDeleteOptionNotAllowed() {
      const { type } = this.slideActive
      if (type === 'multipleChoice'
        || type === 'pickAnswer'
        || type === 'imageChoice') {
        return this.slideActive.SlideOptions.length === 2
      }
      if (type === 'scale') {
        return this.slideActive.SlideOptions.length === 1
      }
      return false
    },
    isCheckCorrectAnswerVisible() {
      const { addCorrectOption, type } = this.slideActive
      return (addCorrectOption && type == 'multipleChoice') || type == 'pickAnswer' || (addCorrectOption && type == 'imageChoice')
    },
    isAllowPickingMoreThanOneOptionsVisible() {
      return this.slideActive.type === 'imageChoice'
        || this.slideActive.type === 'multipleChoice'
    },
    isAddSlideOptionButtonVisible() {
      const { type } = this.slideActive
      const optionsLength = this.slideActive.SlideOptions.length
      return (optionsLength < 30 && (type == 'multipleChoice' || type == 'imageChoice'))
        || (optionsLength < 15 && type == 'bulletPoints')
        || (optionsLength < 8 && type == 'scale')
        || (optionsLength < 8 && type == 'pickAnswer')
    },
    isShowConvertQuizNoteBox() {
      const {
        type,
        addCorrectOption,
        SlideOptions: options,
      } = this.slideActive

      if (
        (type !== 'imageChoice' && type !== 'multipleChoice')
        || !addCorrectOption
      ) return false

      return options && options.filter((option) => option.correct).length > 0
    },
  },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    handleSortOptions(event) {
      this.$emit('sortOption', event)
    },
    isShowCorrectCheckAutocomplete(item) {
      const autoSlideOption = this.autoCompleteSlideOption
        .find((autoItem) => autoItem.randomIndexId == item.randomIndexId)
      return autoSlideOption && autoSlideOption.correct && (!item.title || item.length === 0)
    },
    isHideOptions(index) {
      const { type } = this.slideActive
      return index > 29 ||
        (index > 7 && (type === 'pickAnswer' || type === 'scale')) ||
        (index > 14 && (type === 'bulletPoints'))
    },
    handleUpdateOptionTitle(value) {
      this.$emit('updateTitleOption', value)
    },
    handleUpdateSettingSlide(type, cb = null, paramObj = {}) {
      this.$emit('updateSettingSlide', type, cb, paramObj)
    },

    _updateMultipleChoice() {
      const countCorrectOption = this.slideActive.SlideOptions
        .filter((option) => option.correct)
        .length

      // The issue update slideActiveOption is not being called
      // Assumption: There is a case when correctOption is not 1 or 2
      // Solution: Always update no matter what the correctOption is

      const multipleChoice = countCorrectOption > 1
      const isCorrectGetPoint = countCorrectOption === 2 && !this.slideActive.multipleChoice
      const updatedSlideOptions = {
        multipleChoice,
      }

      if (isCorrectGetPoint) {
        updatedSlideOptions.isCorrectGetPoint = true
      }

      this.updateSlideActiveAction(updatedSlideOptions)
      this.$emit('updateSettingSlide', 1, null, updatedSlideOptions)
    },

    updateCheckOption: function updateCheckOption(item) {
      this.$emit('updateOption', {
        correct: item.correct,
        presentationId: this.presentation.id,
        accessCode: this.presentation.uniqueAccessCode,
      }, item.id)

      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Side right > Check correct option',
        `Is correct option: ${item.correct}`,
      )

      this._updateMultipleChoice()
    },

    checkCorrectOption(item) {
      if (!item.id || (!item.title && !item.image && !item.imageCropped && !item.correct)) {
        this.tooltipCheckCorrect = ''
        item.showErrorCheckCorrect = true
        this.$store.state.slideActive.SlideOptions.push()
        return
      }
      this.tooltipCheckCorrect = MarkAsCorrectAnswer
      /**
       * Immediately mutate store state!!
       * It's work. So I don't want to touch it now.
       */
      item.correct = !item.correct

      this.updateCheckOption(item)
    },
    onClickOutSideCheckCorrect(item) {
      if (!item.showErrorCheckCorrect) return
      this.tooltipCheckCorrect = MarkAsCorrectAnswer
      item.showErrorCheckCorrect = false
      this.$store.state.slideActive.SlideOptions.push()
    },
  },
}
</script>

<style lang="scss" scoped>
$success: #33C173;
$secondary: #B4BCCF;
$white: #fff;
$gray: #ddd;

.input-content-wrapper {
  display: flex;
  flex-grow: 1;

  @media (min-width: 992px) {
    &:hover {
      .checkmark {
        visibility: visible;
      }
    }
  }
}

.check-correct-answer {
  width: 44px;
  height: 44px;
  border: 1px solid $gray;
  border-right: none;
  cursor: pointer;
  position: relative;

  @media (min-width: 992px) {
    &:hover   {
      .checkmark {
        visibility: visible;
        border-color: $success;
        color: $success;
      }
    }
  }

  .checkmark {
    display: flex;
    justify-content: center;
    align-items: center;
    visibility: hidden;
    width: 20px;
    height: 20px;
    border: 2px solid $secondary;
    color: $secondary;
    border-radius: 50%;
    font-size: 10px;
  }

  &.correct {
    .checkmark {
      visibility: visible;
      border-color: $success;
      background-color: $success;
      color: $white;
    }
  }
  &.correct-autocomplete{
    .checkmark{
      visibility: visible;
      border-color:#B5B5B5;
      background-color:#B5B5B5;
      color: $white;
    }
  }
}

ul {
  li {
    list-style: none;
  }
}

::v-deep {
  .input-group {
    flex-wrap: nowrap !important;
  }
}

.input-option {
  & ::v-deep .input-style {
    width: min-content;
    z-index: 1;
  }
}

.add-button {
  @apply w-full flex;
}
</style>
